import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { userService } from "./services/supabaseService";
import Toast from "./components/Toast";
import ConfirmModal from "./components/ConfirmModal";
import "./style.css";

const DEFAULT_AVATAR = "https://i.pinimg.com/736x/d4/8f/8f/d48f8f59f2de25750b13084b59301201.jpg";

function Friends({ onClose, onNav }) {
  const [activeTab, setActiveTab] = useState("solicitacoes");
  const [friendRequests, setFriendRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingRequests, setProcessingRequests] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const showConfirm = (title, message, onConfirm, confirmType = 'danger') => {
    return new Promise((resolve) => {
      setConfirmModal({
        title,
        message,
        confirmType,
        onConfirm: () => {
          setConfirmModal(null);
          resolve(true);
          if (onConfirm) onConfirm();
        },
        onCancel: () => {
          setConfirmModal(null);
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    loadFriendRequests();
    loadFriends();
  }, []);

  const loadFriendRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Buscar solicitações pendentes recebidas
      const { data, error } = await supabase
        .from('friendships')
        .select('id, user_id, created_at, status')
        .eq('friend_id', user.id)
        .eq('status', 'pending');
      
      if (error) {
        throw error;
      }
      
      // Buscar dados dos usuários que enviaram as solicitações
      const requestsWithUserData = await Promise.all(
        (data || []).map(async (req) => {
          const userData = await userService.getUserById(req.user_id);
          return {
            id: req.id,
            from: req.user_id,
            userName: userData?.nome || "Usuário",
            userCity: userData?.cidade || "",
            userPhoto: userData?.avatar || "",
            createdAt: req.created_at
          };
        })
      );
      
      setFriendRequests(requestsWithUserData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const loadFriends = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const friendsData = await userService.getFriends(user.id);
      setFriends(friendsData.map(friend => ({
        id: friend.id,
        nome: friend.nome || "Usuário",
        cidade: friend.cidade || "",
        photoURL: friend.avatar || "",
        universidade: friend.universidade || "",
        since: friend.friendship_created_at || friend.created_at || new Date().toISOString()
      })));
    } catch (err) {
    }
  };

  const handleAccept = async (request) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        showToast("Erro: Usuário não autenticado", "error");
        return;
      }

      // Adicionar ao set de processamento
      setProcessingRequests(prev => new Set([...prev, request.id]));

      // Aceitar solicitação de amizade
      const success = await userService.acceptFriendRequest(user.id, request.from);
      
      if (success) {
        showToast("Solicitação aceita! 🎉", "success");
        
        // Remover da lista de solicitações
        setFriendRequests(prev => prev.filter(req => req.id !== request.id));
        
        // Recarregar lista de amigos
        await loadFriends();
      } else {
        showToast("Erro ao aceitar solicitação. Verifique as permissões.", "error");
        // Reverter se falhou
        await loadFriendRequests();
      }
    } catch (err) {
      showToast(`Erro: ${err.message || 'Tente novamente'}`, "error");
      // Recarregar em caso de erro para reverter mudanças
      await loadFriendRequests();
    } finally {
      // Remover do set de processamento
      setProcessingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(request.id);
        return newSet;
      });
    }
  };

  const handleReject = async (requestId, fromUserId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Adicionar ao set de processamento
      setProcessingRequests(prev => new Set([...prev, requestId]));

      // Primeiro remove da lista imediatamente para feedback visual
      setFriendRequests(prev => prev.filter(req => req.id !== requestId));

      // Remover solicitação
      const success = await userService.removeFriendship(fromUserId, user.id);
      
      if (success) {
        showToast("Solicitação recusada", "info");
      } else {
        showToast("Erro ao recusar solicitação", "error");
        // Reverter se falhou
        await loadFriendRequests();
      }
    } catch (err) {
      showToast("Erro ao recusar. Tente novamente.", "error");
      // Recarregar em caso de erro para reverter mudanças
      await loadFriendRequests();
    } finally {
      // Remover do set de processamento
      setProcessingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  const handleRemoveFriend = async (friendId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const confirmed = await showConfirm(
        "Remover Amigo",
        "Tem certeza que deseja remover este amigo?",
        null,
        "danger"
      );
      
      if (!confirmed) return;

      const success = await userService.removeFriendship(user.id, friendId);
      
      if (success) {
        await loadFriends();
        showToast("Amigo removido", "success");
      } else {
        showToast("Erro ao remover amigo", "error");
      }
    } catch (err) {
      showToast("Erro ao remover. Tente novamente.", "error");
    }
  };

  return (
    <div className="orkut-friends-container">
      <div className="mobile-filter-bar" style={{ 
        background: "#fff", 
        padding: "10px", 
        borderRadius: "5px", 
        marginBottom: "20px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
      }}>
        <button 
          className={`mobile-filter-btn ${activeTab === "solicitacoes" ? "active" : ""}`}
          onClick={() => setActiveTab("solicitacoes")}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0"></path>
          </svg>
          Solicitações ({friendRequests.length})
        </button>
        <button 
          className={`mobile-filter-btn ${activeTab === "amigos" ? "active" : ""}`}
          onClick={() => setActiveTab("amigos")}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
          Meus Amigos ({friends.length})
        </button>
      </div>

      <div className="orkut-friends-content">
        {loading ? (
          <div className="orkut-friends-loading">Carregando...</div>
        ) : activeTab === "solicitacoes" ? (
          <div className="orkut-friend-requests">
            {friendRequests.length === 0 ? (
              <div className="orkut-friends-empty">
                Nenhuma solicitação pendente
              </div>
            ) : (
              friendRequests.map(request => (
                <div key={request.id} className="orkut-friend-request-item">
                  <div 
                    className="orkut-friend-request-avatar"
                    style={request.userPhoto ? {
                      backgroundImage: `url(${request.userPhoto})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      fontSize: '0'
                    } : {}}
                  >
                    {!request.userPhoto && (
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    )}
                  </div>
                  <div className="orkut-friend-request-info">
                    <div className="orkut-friend-request-name">{request.userName}</div>
                    {request.userCity && (
                      <div className="orkut-friend-request-city" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        {request.userCity}
                      </div>
                    )}
                  </div>
                  <div className="orkut-friend-request-actions">
                    <button 
                      className="orkut-btn-accept"
                      onClick={() => handleAccept(request)}
                      disabled={processingRequests.has(request.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}
                    >
                      {processingRequests.has(request.id) ? 'Aceitando...' : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          Aceitar
                        </>
                      )}
                    </button>
                    <button 
                      className="orkut-btn-reject"
                      onClick={() => handleReject(request.id, request.from)}
                      disabled={processingRequests.has(request.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}
                    >
                      {processingRequests.has(request.id) ? 'Recusando...' : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                          Recusar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="orkut-friends-list">
            {friends.length === 0 ? (
              <div className="orkut-friends-empty">
                Você ainda não tem amigos. Comece a adicionar pessoas!
              </div>
            ) : (
              friends.map(friend => (
                <div key={friend.id} className="orkut-friend-item">
                  <div 
                    className="orkut-friend-avatar"
                    onClick={() => {
                      if (onNav) {
                        onClose(); // Fecha o modal de amigos
                        onNav("viewProfile", friend.id); // Navega para o perfil
                      }
                    }}
                    style={{
                      backgroundImage: `url(${friend.photoURL || DEFAULT_AVATAR})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      cursor: 'pointer'
                    }}
                  />
                  <div className="orkut-friend-info">
                    <div 
                      className="orkut-friend-name"
                      onClick={() => {
                        if (onNav) {
                          onClose(); // Fecha o modal de amigos
                          onNav("viewProfile", friend.id); // Navega para o perfil
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {friend.nome}
                    </div>
                    {friend.cidade && (
                      <div className="orkut-friend-city" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        {friend.cidade}
                      </div>
                    )}
                    {friend.since && (
                      <div className="orkut-friend-since">
                        Amigos desde {new Date(friend.since).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </div>
                    )}
                  </div>
                  <button 
                    className="orkut-btn-remove-friend"
                    onClick={() => handleRemoveFriend(friend.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3,6 5,6 21,6"/>
                      <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                      <line x1="10" y1="11" x2="10" y2="17"/>
                      <line x1="14" y1="11" x2="14" y2="17"/>
                    </svg>
                    Remover
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Toast Profissional */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Modal de Confirmação Profissional */}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmType={confirmModal.confirmType}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}
    </div>
  );
}

export default Friends;
